import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import SendingPreferenceSection from './SendingPreferenceSection';
import PostalOnlySection from './PostalOnlySection';
import PriorityPostSection from './PriorityPostSection';
import SingleSidedSection from './SingleSidedSection';

const AdvancedOptionsSection = ({
  formData,
  handleInputChange,
  sendingPreferenceOptions,
  showSendingPreferenceModal,
  setShowSendingPreferenceModal,
  pendingSendingPreference,
  setPendingSendingPreference,
  showPriorityModal,
  setShowPriorityModal,
  pendingPriority,
  setPendingPriority,
  showSingleSidedModal,
  setShowSingleSidedModal,
  pendingSingleSided,
  setPendingSingleSided,
  isDark,
  showStrataOptions = true,
  advancedTrigger = null
}) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [openAccordions, setOpenAccordions] = useState({});

  useEffect(() => {
    if (!advancedTrigger) return;
    if (advancedTrigger.expand) {
      setIsEnabled(true);
      setOpenAccordions(prev => ({ ...prev, commPref: true }));
    } else {
      setIsEnabled(false);
      setOpenAccordions({});
    }
  }, [advancedTrigger]);

  const toggleAccordion = (key) => {
    setOpenAccordions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleToggle = () => {
    setIsEnabled(prev => !prev);
    if (isEnabled) {
      setOpenAccordions({});
    }
  };

  // Matches SendingSection AccordionItem wrapper styling
  const getWrapperClasses = (isOpen) => {
    if (isOpen) {
      return isDark
        ? 'bg-[#000000]'
        : 'bg-white border border-solid border-[1px] border-[#d9d9d9]';
    }
    return isDark
      ? 'bg-[#161515]'
      : 'bg-[#f5f5f5]';
  };

  // Matches SendingSection AccordionTrigger styling
  const getAccordionHeaderClasses = (isOpen) => {
    if (isOpen) {
      return isDark
        ? 'bg-[#1a1a1a] rounded-t-[8px]'
        : 'bg-[#fff5eb] rounded-t-[8px]';
    }
    return isDark
      ? 'bg-[#161515] rounded-[8px]'
      : 'bg-[#f5f5f5] rounded-[8px]';
  };

  // Text always font-medium, color matches SendingSection trigger text
  const getAccordionTextClasses = () => {
    return `font-medium ${isDark ? 'text-white' : 'text-[#072837]'}`;
  };

  // Chevron color matches SendingSection [&>svg] styling
  const getChevronClasses = (isOpen) => {
    const rotate = isOpen ? 'rotate-180' : '';
    const color = isDark ? 'text-white' : 'text-[#072837]';
    return `h-[12px] w-[12px] shrink-0 transition-transform duration-200 ${rotate} ${color}`;
  };

  return (
    <div className={`rounded-[10px] p-[20px] w-full max-w-[822px] flex flex-col gap-[16px] transition-colors duration-200 ${
      isDark ? 'bg-[#000000]' : 'bg-white'
    }`} style={{ boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.11)' }}>

      {/* Header section */}
      <div className="flex flex-col gap-[16px]">
        {/* Title */}
        <div className={`text-[16px] font-semibold transition-colors duration-200 ${
          isDark ? 'text-white' : 'text-[#072837]'
        }`} style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Advanced Options
        </div>

        {/* Toggle row */}
        <div className={`flex flex-row justify-between items-center p-[12px] rounded-[8px] transition-colors duration-200 ${
          isDark ? 'bg-[#161515]' : 'bg-[#f5f5f5] border-solid border-[1px] border-[#f8f9fa]'
        }`}>
          <span className={`text-[16px] font-medium transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-[#072837]'
          }`} style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Configure delivery method, postal handling, and print settings
          </span>

          {/* Toggle switch */}
          <button
            type="button"
            onClick={handleToggle}
            className={`relative flex items-center w-[40px] h-[22px] rounded-full p-[2px] transition-colors duration-200 cursor-pointer border-none ${
              isEnabled ? 'bg-[#CD6D2C]' : isDark ? 'bg-[#555555]' : 'bg-[#B3B3B3]'
            }`}
            style={{ flexShrink: 0 }}
          >
            <span
              className={`block w-[18px] h-[18px] bg-white rounded-full transition-transform duration-200 ${
                isEnabled ? 'translate-x-[18px]' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Accordions - only shown when toggle is enabled */}
      {isEnabled && (
        <div className="flex flex-col gap-[8px]">

          {/* Communication Preference Override - Strata only */}
          {showStrataOptions && (
          <div className={`rounded-[8px] overflow-hidden transition-colors duration-200 ${getWrapperClasses(openAccordions.commPref)}`}>
            <button
              type="button"
              onClick={() => toggleAccordion('commPref')}
              className={`w-full flex flex-row justify-between items-center p-[12px] cursor-pointer border-none m-0 transition-colors duration-200 ${getAccordionHeaderClasses(openAccordions.commPref)}`}
            >
              <span className={`text-[16px] text-left transition-colors duration-200 ${getAccordionTextClasses()}`} style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Communication Preference Override
              </span>
              <ChevronDown className={getChevronClasses(openAccordions.commPref)} />
            </button>

            {openAccordions.commPref && (
              <div className={`p-[20px] flex flex-col gap-[16px] transition-colors duration-200 ${
                isDark ? 'bg-[#000000]' : 'bg-white'
              }`}>
                <SendingPreferenceSection
                  formData={formData}
                  handleInputChange={handleInputChange}
                  sendingPreferenceOptions={sendingPreferenceOptions}
                  showSendingPreferenceModal={showSendingPreferenceModal}
                  setShowSendingPreferenceModal={setShowSendingPreferenceModal}
                  pendingSendingPreference={pendingSendingPreference}
                  setPendingSendingPreference={setPendingSendingPreference}
                  isDark={isDark}
                  isEmbedded={true}
                />
              </div>
            )}
          </div>
          )}

          {/* Sending to Postal recipients only - Strata only */}
          {showStrataOptions && (
          <div className={`rounded-[8px] overflow-hidden transition-colors duration-200 ${getWrapperClasses(openAccordions.postalOnly)}`}>
            <button
              type="button"
              onClick={() => toggleAccordion('postalOnly')}
              className={`w-full flex flex-row justify-between items-center p-[12px] cursor-pointer border-none m-0 transition-colors duration-200 ${getAccordionHeaderClasses(openAccordions.postalOnly)}`}
            >
              <span className="text-left flex flex-col gap-[2px]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                <span className={`text-[16px] transition-colors duration-200 ${getAccordionTextClasses()}`}>
                  Sending to Postal recipients only
                </span>
                <span className={`text-[12px] font-normal transition-colors duration-200 ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`}>
                  Select "Postal" in Communication Preference Override to enable this option
                </span>
              </span>
              <ChevronDown className={getChevronClasses(openAccordions.postalOnly)} />
            </button>

            {openAccordions.postalOnly && (
              <div className={`p-[20px] flex flex-col gap-[16px] transition-colors duration-200 ${
                isDark ? 'bg-[#000000]' : 'bg-white'
              }`}>
                <PostalOnlySection
                  formData={formData}
                  handleInputChange={handleInputChange}
                  isDark={isDark}
                  isEmbedded={true}
                />
              </div>
            )}
          </div>
          )}

          {/* Priority Post */}
          <div className={`rounded-[8px] overflow-hidden transition-colors duration-200 ${getWrapperClasses(openAccordions.priorityPost)}`}>
            <button
              type="button"
              onClick={() => toggleAccordion('priorityPost')}
              className={`w-full flex flex-row justify-between items-center p-[12px] cursor-pointer border-none m-0 transition-colors duration-200 ${getAccordionHeaderClasses(openAccordions.priorityPost)}`}
            >
              <span className={`text-[16px] text-left transition-colors duration-200 ${getAccordionTextClasses()}`} style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Priority Post – additional cost will be incurred
              </span>
              <ChevronDown className={getChevronClasses(openAccordions.priorityPost)} />
            </button>

            {openAccordions.priorityPost && (
              <div className={`p-[20px] flex flex-col gap-[16px] transition-colors duration-200 ${
                isDark ? 'bg-[#000000]' : 'bg-white'
              }`}>
                <PriorityPostSection
                  formData={formData}
                  handleInputChange={handleInputChange}
                  showPriorityModal={showPriorityModal}
                  setShowPriorityModal={setShowPriorityModal}
                  pendingPriority={pendingPriority}
                  setPendingPriority={setPendingPriority}
                  isDark={isDark}
                  isEmbedded={true}
                />
              </div>
            )}
          </div>

          {/* Single-Sided Printing */}
          <div className={`rounded-[8px] overflow-hidden transition-colors duration-200 ${getWrapperClasses(openAccordions.singleSided)}`}>
            <button
              type="button"
              onClick={() => toggleAccordion('singleSided')}
              className={`w-full flex flex-row justify-between items-center p-[12px] cursor-pointer border-none m-0 transition-colors duration-200 ${getAccordionHeaderClasses(openAccordions.singleSided)}`}
            >
              <span className={`text-[16px] text-left transition-colors duration-200 ${getAccordionTextClasses()}`} style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Single-Sided Printing
              </span>
              <ChevronDown className={getChevronClasses(openAccordions.singleSided)} />
            </button>

            {openAccordions.singleSided && (
              <div className={`p-[20px] flex flex-col gap-[16px] transition-colors duration-200 ${
                isDark ? 'bg-[#000000]' : 'bg-white'
              }`}>
                <SingleSidedSection
                  formData={formData}
                  handleInputChange={handleInputChange}
                  showSingleSidedModal={showSingleSidedModal}
                  setShowSingleSidedModal={setShowSingleSidedModal}
                  pendingSingleSided={pendingSingleSided}
                  setPendingSingleSided={setPendingSingleSided}
                  isDark={isDark}
                  isEmbedded={true}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedOptionsSection;
